import React, { useState, useEffect } from 'react';
import { useAuth, API } from '@/App';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Syringe, Shield, ArrowRight, Mail, Loader2, ArrowLeft, KeyRound, Eye, EyeOff, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const LoginPage = () => {
  const { user, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState('choice'); // 'choice' | 'password' | 'pending'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pendingMessage, setPendingMessage] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const errMsg = params.get('error');
    if (errMsg) {
      toast.error(errMsg, { duration: 8000 });
      window.history.replaceState(null, '', '/login');
    }
  }, [location.search]);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePasswordLogin = async (e) => {
    e?.preventDefault();
    const normalized = (email || '').trim().toLowerCase();
    if (!normalized || !normalized.includes('@')) {
      toast.error('Ingresa un correo válido');
      return;
    }
    if (!password) {
      toast.error('Ingresa tu contraseña');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/auth/login-password`,
        { email: normalized, password },
        { withCredentials: true }
      );
      toast.success('Acceso concedido');
      await checkAuth();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const msg = typeof detail === 'string' ? detail : 'Error al iniciar sesión';
      // Special case: first login validated but pending admin approval
      if (status === 403 && /aprobaci/i.test(msg)) {
        setPendingMessage(msg);
        setMode('pending');
        setPassword('');
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="loading-spinner w-10 h-10"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/6183677/pexels-photo-6183677.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Medical background"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/80 to-slate-900/90" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <div className="max-w-md">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Syringe className="w-7 h-7 text-white" />
              </div>
              <h1 className="text-2xl font-bold heading">Vacu-Track</h1>
              <p className="text-xs text-slate-500">by ExtremeBits</p>
            </div>
            <h2 className="text-4xl font-bold mb-4 heading leading-tight">
              Sistema de Gestión de Vacunatorio
            </h2>
            <p className="text-lg text-blue-100 leading-relaxed">
              Administra registros de vacunación, controla tu caja diaria y genera reportes de manera eficiente y segura.
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center">
              <Syringe className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 heading">Vacu-Track</h1>
            <p className="text-xs text-slate-500">by ExtremeBits</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-900 heading mb-2">
                {mode === 'pending' ? 'Cuenta pendiente' : 'Bienvenido'}
              </h2>
              <p className="text-slate-500">
                {mode === 'choice' && 'Inicia sesión para acceder al sistema'}
                {mode === 'password' && 'Ingresa tu correo y contraseña'}
                {mode === 'pending' && 'Espera la aprobación del administrador'}
              </p>
            </div>

            {mode === 'choice' && (
              <>
                <Button
                  onClick={handleGoogleLogin}
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-lg flex items-center justify-center gap-3 transition-all hover:shadow-lg active:scale-[0.98]"
                  data-testid="google-login-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continuar con Google
                  <ArrowRight className="w-4 h-4" />
                </Button>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200"></div>
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-slate-400">O</span>
                  </div>
                </div>

                <Button
                  onClick={() => setMode('password')}
                  variant="outline"
                  className="w-full h-12 border-slate-300 hover:bg-slate-50 text-slate-700 font-medium rounded-lg flex items-center justify-center gap-3"
                  data-testid="password-login-btn"
                >
                  <KeyRound className="w-5 h-5" />
                  Iniciar con correo y contraseña
                </Button>
              </>
            )}

            {mode === 'password' && (
              <form onSubmit={handlePasswordLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Correo</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="tu@correo.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoFocus
                    autoComplete="email"
                    required
                    data-testid="login-email-input"
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-pw">Contraseña</Label>
                  <div className="relative">
                    <Input
                      id="login-pw"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Tu contraseña"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                      data-testid="login-password-input"
                      className="h-12 pr-11 font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      tabIndex={-1}
                      aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">
                    La contraseña la genera el sistema y te llega por correo cuando el administrador te da de alta.
                  </p>
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg flex items-center justify-center gap-2"
                  data-testid="submit-password-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin"/> : <ArrowRight className="w-4 h-4"/>}
                  Iniciar sesión
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => { setMode('choice'); setPassword(''); }}
                  className="w-full text-slate-500 hover:text-slate-700"
                >
                  <ArrowLeft className="w-4 h-4 mr-2"/> Volver
                </Button>
              </form>
            )}

            {mode === 'pending' && (
              <div className="space-y-4">
                <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-4 flex gap-3">
                  <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5"/>
                  <div>
                    <p className="text-sm text-amber-900 font-medium">Ingreso validado</p>
                    <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                      {pendingMessage || 'Tu cuenta quedó pendiente de aprobación por el administrador. Recibirás un correo cuando esté activa.'}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => { setMode('choice'); setEmail(''); }}
                  variant="outline"
                  className="w-full h-12"
                >
                  <ArrowLeft className="w-4 h-4 mr-2"/> Volver al inicio
                </Button>
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-slate-100">
              <div className="flex items-center gap-3 text-sm text-slate-500">
                <Shield className="w-5 h-5 text-emerald-500"/>
                <span>Acceso restringido a usuarios autorizados</span>
              </div>
            </div>
          </div>

          <p className="text-center text-xs text-slate-400 mt-6">
            Al iniciar sesión, aceptas los términos de uso y política de privacidad.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
